import { Component, computed, inject, signal } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import { SidebarComponent } from '../sidebar/sidebar.component';
import { NavbarComponent } from '../navbar/navbar.component';
import { LogoutModalComponent } from '../logout-modal/logout-modal.component';
import { LoginModalComponent } from '../../features/landing/login-modal/login-modal.component';
import { SignUpModalComponent } from '../../features/landing/sign-up-modal/sign-up-modal.component';
import { ForgotPasswordModalComponent } from '../../features/landing/forgot-password-modal/forgot-password-modal.component';
import { AuthService } from '../../core/services/auth.service';
import { AuthOverlayService } from '../../core/services/auth-overlay.service';
import { isAdminRole } from '../../core/utils/admin-role';
import { LandingNavbarComponent } from '../../features/landing/landing-navbar/landing-navbar.component';

const ROUTE_TITLES: Record<string, string> = {
  explore: 'Explorar',
  wishlists: 'Minhas Listas',
  friends: 'Amigos',
  reservations: 'Itens Reservados',
  settings: 'Configurações',
  privacy: 'Política de privacidade',
  terms: 'Termos de serviço',
};

const ADMIN_ROUTE_TITLES: Record<string, string> = {
  '': 'Início',
  administrators: 'Administradores',
  'partner-stores': 'Lojas Parceiras',
  users: 'Usuários',
  reports: 'Denúncias',
};

@Component({
  selector: 'app-main-layout',
  standalone: true,
  imports: [
    RouterOutlet,
    SidebarComponent,
    NavbarComponent,
    LogoutModalComponent,
    LoginModalComponent,
    SignUpModalComponent,
    ForgotPasswordModalComponent,
    LandingNavbarComponent,
  ],
  templateUrl: './main-layout.component.html',
  styleUrl: './main-layout.component.scss',
})
export class MainLayoutComponent {
  private router = inject(Router);
  private authService = inject(AuthService);
  readonly authOverlay = inject(AuthOverlayService);

  /** Barra superior omitida na área admin (papel ADMIN). */
  readonly showNavbar = computed(() => !isAdminRole(this.authService.currentUser()?.role));

  logoutModalOpen = signal(false);
  private url = toSignal(this.router.events.pipe(
    map(() => this.router.url),
  ), { initialValue: this.router.url });

  private readonly pathOnly = computed(() => {
    const raw = this.url() ?? '';
    return raw.split('?')[0].split('#')[0] || '/';
  });

  /** Visitante em /privacy ou /terms: navbar da landing, sem sidebar nem navbar do app. */
  readonly guestLegalDocumentLayout = computed(
    () => !this.authService.isAuthenticated() && /^\/(privacy|terms)\/?$/.test(this.pathOnly()),
  );

  pageTitle = computed(() => {
    const url = this.url() ?? '';
    const segment = url.split('?')[0].replace(/^\/+|\/+$/g, '') || '';
    const parts = segment.split('/');
    if (parts[0] === 'profile' && parts[1] === 'edit') {
      return 'Editar perfil';
    }
    if (parts[0] === 'settings' && parts[1] === 'notifications') {
      return 'Notificações';
    }
    if (parts[0] === 'settings' && parts[1] === 'password') {
      return 'Senha e privacidade';
    }
    if (parts[0] === 'settings' && parts[1] === 'faq') {
      return 'Perguntas frequentes';
    }
    if (parts[0] === 'user' && parts[2] === 'wishlists') {
      return 'Listas';
    }
    if (parts[0] === 'admin') {
      const sub = parts[1] ?? '';
      if (sub === 'users' && (parts[2] ?? '').trim() !== '') {
        return 'Ver Listas';
      }
      return ADMIN_ROUTE_TITLES[sub] ?? 'Início';
    }
    const key = parts[0] || 'wishlists';
    return ROUTE_TITLES[key] ?? 'Minhas Listas';
  });

  onLogoutRequested(): void {
    this.logoutModalOpen.set(true);
  }

  closeLogoutModal(): void {
    this.logoutModalOpen.set(false);
  }

  onLogoutConfirm(): void {
    this.authService.logout();
    this.logoutModalOpen.set(false);
  }

  onOverlayLoginSuccess(): void {
    this.authOverlay.closeLogin();
    this.navigateAfterAuth();
  }

  onOverlayRegisterSuccess(): void {
    this.authOverlay.closeSignup();
    this.navigateAfterAuth();
  }

  onOverlayForgotPassword(): void {
    this.authOverlay.requestForgotPassword();
  }

  private navigateAfterAuth(): void {
    if (isAdminRole(this.authService.currentUser()?.role)) {
      void this.router.navigateByUrl('/admin');
      return;
    }
    const pathOnly = this.router.url.split('?')[0].split('#')[0];
    if (/^\/wishlists\/[^/]+\/?$/.test(pathOnly)) {
      return;
    }
    void this.router.navigateByUrl('/wishlists');
  }
}
